<script setup>
import { Avatar } from '@dashhudson/dashing-ui/avatar';
import { Button } from '@dashhudson/dashing-ui/button';
import { SelectList } from '@dashhudson/dashing-ui/select-list';
import { useElementSize } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';

import { MIXPANEL_VALUES } from '@/components/layout/navigation/constants';
import { useBrandSwitcher } from '@/composables/useBrandSwitcher';
import { ORGANIZATION } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { useChiliPiperStore } from '@/stores/chili-piper';
import { trackNavigation } from '@/utils/analytics/tracking';
import { getBrandInitials } from '@/utils/brand';

const authStore = useAuthStore();
const chiliPiperStore = useChiliPiperStore();

const { switchBrand } = useBrandSwitcher();

const emits = defineEmits(['selected']);

const selectedBrand = ref(authStore.currentBrand);
const selectList = ref(null);

const selectListWrapper = ref(null);
const { height } = useElementSize(selectListWrapper);
const selectListWrapperInitialHeight = ref(null);

const brandList = computed(() => {
  if (authStore.identity) {
    const brands = Object.keys(authStore.identity.brands)
      .map((brandLabel) => ({
        value: authStore.identity.brands[brandLabel],
        label: authStore.identity.brands[brandLabel].name,
        icon: authStore.identity.brands[brandLabel].avatarUrl,
      }))
      .filter((brand) => brand.value.id !== authStore.currentBrand.id);
    return [
      {
        value: authStore.currentBrand,
        label: authStore.currentBrand.name,
        icon: authStore.currentBrand.avatarUrl,
      },
      ...brands,
    ];
  }
  return [];
});

const canAddNewBrand = computed(() => authStore.guard(ORGANIZATION.GENERAL.CAN_ADD_NEW_BRAND));

function selectBrand(selectedBrandValue) {
  try {
    switchBrand(selectedBrandValue.label);
  } finally {
    emits('selected');
  }
  trackNavigation(MIXPANEL_VALUES.UTILITY_MENU.ROOT, MIXPANEL_VALUES.UTILITY_MENU.SWITCH_BRAND);
}

function trackSearch(to, from) {
  if (to && to.length === 1 && !from) {
    trackNavigation(MIXPANEL_VALUES.UTILITY_MENU.ROOT, MIXPANEL_VALUES.UTILITY_MENU.SEARCH_BRAND);
  }
}

function addNewBrand() {
  chiliPiperStore.submit('add-social-set', 'add_social_set');
}

onMounted(() => {
  selectListWrapperInitialHeight.value = height.value;

  // This is a workaround because of how DropdownBase (FloatingVue) works currently.
  setTimeout(() => {
    selectList.value.$el.querySelector('input').focus();
  }, 200);
});
</script>

<template>
  <div
    ref="selectListWrapper"
    class="dash-bg-background-page !flex !w-[var(--secondary-navigation-width)] !px-4 !py-4 [&_label]:!w-full [&_li]:min-h-[2.625rem] [&_li_>div]:w-full [&_ul]:max-h-[33.5rem] [&_ul]:overflow-auto"
    :style="{ minHeight: `${selectListWrapperInitialHeight}px` }"
  >
    <SelectList
      ref="selectList"
      v-model="selectedBrand"
      :options="brandList"
      :searchable="authStore.canAccessMultipleBrands"
      empty-text="No brands found"
      hide-controls
      search-placeholder="Search brand"
      :display-options-limit="3"
      class="cy-test--brand-switcher-select-list !w-full !max-w-none"
      @update:model-value="selectBrand"
      @search="trackSearch"
    >
      <template v-if="canAddNewBrand" #header>
        <div
          class="dash-single-select-option dash-px-3 dash-py-2 dash-flex dash-items-center dash-rounded dash-cursor-pointer dash-typo-base hover:dash-bg-background-secondary-hover dash-justify-between"
          @click="addNewBrand"
        >
          <Button
            class="!mx-3 !my-[5px] !font-medium !no-underline"
            label="Add New"
            text
            :icon="{
              ariaLabel: 'plus',
              name: 'plus',
              size: 20,
            }"
            severity="secondary"
            data-test="AddNewBrand"
          />
        </div>
      </template>
      <template #optionLabel="{ option, label }">
        <div
          v-dash-tooltip="{ content: label, delay: { show: 800, hide: 0 } }"
          class="!flex !w-full !items-center !gap-2"
        >
          <Avatar
            :image="option.icon"
            :label="option.icon ? null : getBrandInitials(option.value)"
            size="sm"
            class="!ml-3 !h-5 !w-5 !shrink-0 !cursor-pointer"
          />
          <p
            class="dash-typo-base-medium !max-w-44 !overflow-hidden !text-ellipsis"
            :class="selectedBrand.name === label ? 'dash-text-primary' : 'dash-text-subtle'"
          >
            {{ label }}
          </p>
        </div>
      </template>
    </SelectList>
  </div>
</template>
